import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { IMedia } from '../../shared/models/interfaces/IMedia';
import { environment } from '../../../environments/environment';
import { IDocument } from '../models/interfaces/IDocument';
import { IVessel, IVesselCategory } from '../models/interfaces/IVessel';
import { IVesselDetail } from '../models/interfaces/IVesselDetail';
import { IVesselExport } from '../models/interfaces/IVesselExport';
import { IVesselFilter } from '../models/interfaces/IVesselFilter';
import {
  IVesselTimeline,
  IVesselTimelineStep,
  IVesselTimelineStepItem,
} from '../models/interfaces/IVesselTimeline';
import { IVesselUpdate } from '../models/interfaces/IVesselUpdate';
import { IVesselOperational } from '../models/interfaces/IVesselOperational';
import { IVesselFinancial } from '../models/interfaces/IVesselFinancial';
import { IVesselCost } from '../models/interfaces/IVesselCost';
import {
  IDailyMovements,
  IDailyPeriod,
} from '../../shared/models/interfaces/IDailyMovements';
import { IVesselTowage } from '../models/interfaces/IVesselTowage';
import { DatePipe } from '@angular/common';

const { apiUrl } = environment;

@Injectable({
  providedIn: 'root',
})
export class VesselService {
  constructor(private http: HttpClient, private datePipe: DatePipe) {}

  list(filter?: IVesselFilter) {
    let params = new HttpParams();

    filter?.ports?.map((port) => {
      params = params.append('ports', port.id);
    });

    filter?.status?.map((status) => {
      params = params.append('status', status.id);
    });

    return this.http.get<IVessel[]>(`${apiUrl}vessels`, { params }).toPromise();
  }

  get(id: string) {
    return this.http.get<IVesselDetail>(`${apiUrl}vessels/${id}`).toPromise();
  }

  getUpdates(id: string) {
    return this.http
      .get<IVesselUpdate[]>(`${apiUrl}vessels/${id}/updates`)
      .toPromise();
  }

  getStepConclusion(step: IVesselTimelineStep) {
    var stepPerc, stepQuantity: any, stepComplete: any;
    stepPerc = stepQuantity = stepComplete = 0;
    if (step && step.itens) {
      step.itens.forEach((item) => {
        // descarta o campo tipo String do calculo
        if (item.tipo != 'TEXTO') {
          stepQuantity++;
          if (
            (item.tipo == 'FLAG' && item.valor == 'V') ||
            (item.tipo == 'DATA' && item.valor)
          ) {
            stepComplete++;
          }
        }
      });
      if (stepQuantity > 0) {
        stepPerc = (stepComplete / stepQuantity) * 100;
      } else {
        //verifica se o step nao tem itens e considera concluido
        stepPerc = 100;
      }
    } else {
      stepPerc = 100;
    }
    return stepPerc;
  }

  async getTimeline(id: String): Promise<IVesselTimeline> {
    var response: any = await this.http
      .get<IVesselTimeline>(`${apiUrl}checklist/${id}`)
      .toPromise();

    // se response vazio {}, seta como null
    if (Object.keys(response).length == 0) {
      response = null!;
    } else {
      // sera preciso ver na sequencia para marcar como concluido somente se o anterior estiver concluido
      response.PreArrival.conclusaoPercentual = this.getStepConclusion(
        response.PreArrival
      );
      if (response.PreArrival.conclusaoPercentual == 100) {
        response.PreArrival.concluido = true;
      }
      response.Arrival.conclusaoPercentual = this.getStepConclusion(
        response.Arrival
      );
      if (
        response.Arrival.conclusaoPercentual == 100 &&
        response.PreArrival.concluido
      ) {
        response.Arrival.concluido = true;
      }
      response.Berthing.conclusaoPercentual = this.getStepConclusion(
        response.Berthing
      );
      if (
        response.Berthing.conclusaoPercentual == 100 &&
        response.Arrival.concluido
      ) {
        response.Berthing.concluido = true;
      }
      response.Unberthing.conclusaoPercentual = this.getStepConclusion(
        response.Unberthing
      );
      if (
        response.Unberthing.conclusaoPercentual == 100 &&
        response.Berthing.concluido
      ) {
        response.Unberthing.concluido = true;
      }
      response.Sailing.conclusaoPercentual = this.getStepConclusion(
        response.Sailing
      );
      if (
        response.Sailing.conclusaoPercentual == 100 &&
        response.Unberthing.concluido
      ) {
        response.Sailing.concluido = true;
      }

      response.conclusao =
        (response.PreArrival.conclusaoPercentual +
          response.Arrival.conclusaoPercentual +
          response.Berthing.conclusaoPercentual +
          response.Unberthing.conclusaoPercentual +
          response.Sailing.conclusaoPercentual) /
        5;
    }

    return response;
  }

  getMedias(id: string) {
    return this.http.get<IMedia[]>(`${apiUrl}vessels/${id}/medias`).toPromise();
  }

  getDocuments(id: string) {
    return this.http
      .get<IDocument[]>(`${apiUrl}vessels/${id}/documents`)
      .toPromise();
  }

  getOperational(id: string) {
    return this.http
      .get<IVesselOperational>(`${apiUrl}vessels/${id}/operational`)
      .toPromise();
  }

  async getDailyPeriods(id: string): Promise<IDailyPeriod[]> {
    var response: any = await this.http
      .get(`${apiUrl}daily/${id}/periodos`)
      .toPromise();

    var periods = response['data'];

    periods.forEach((e: any) => {
      var start = this.datePipe.transform(e.inicio, 'short');
      var end = this.datePipe.transform(e.fim, 'short');

      e.texto = `${start} - ${end}`;
    });

    periods.reverse();

    return periods;
  }

  async getDailyMovements(id: string, period: number) {
    var response: any = await this.http
      .get<IDailyMovements>(`${apiUrl}daily/${id}/periodo/${period}`)
      .toPromise();

    return response['data'];
  }

  getFinancial(id: string) {
    return this.http
      .get<IVesselFinancial>(`${apiUrl}vessels/${id}/financial`)
      .toPromise();
  }

  getCosts(id: string) {
    return this.http
      .get<IVesselCost[]>(`${apiUrl}vessels/${id}/custeios`)
      .toPromise();
  }

  downloadMedia(vesselId: string, mediaId: number) {
    return this.http
      .get(`${apiUrl}vessels/${vesselId}/medias/${mediaId}`, {
        responseType: 'blob',
      })
      .toPromise();
  }

  downloadDocument(vesselId: string, documentId: number) {
    return this.http
      .get(`${apiUrl}vessels/${vesselId}/documents/${documentId}`, {
        responseType: 'blob',
      })
      .toPromise();
  }

  export(vessels: string[]): Promise<IVesselExport[]> {
    return this.http
      .post<IVesselExport[]>(`${apiUrl}vessels/export`, {
        vessels: vessels.map((e) => {
          return {
            nrReferencia: e,
          };
        }),
      })
      .toPromise();
  }

  getCategories(): Promise<IVesselCategory[]> {
    // const response = this.http
    //   .get<IVesselCategory[]>(`${apiUrl}/admin/navios/categorias`)
    //   .toPromise();
    const response = this.http
      .get<IVesselCategory[]>(`${apiUrl}vessels/categorias/all`)
      .toPromise();

    return response;
  }

  getTowages(vessel: IVesselDetail): Promise<IVesselTowage[]> {
    return this.http
      .post<IVesselTowage[]>(`${apiUrl}eventosrebocador`, {
        dataSaidaNavio: new Date(
          vessel.dates.find((date) => date.type === 'ETB')?.value ?? ''
        ),
        imo: parseInt(vessel.imo),
        codigoPorto: vessel.portId,
      })
      .toPromise();
  }
}
